/**
 * React Query hooks for GHL OAuth token health in the admin dashboard.
 *
 * - useTokenHealthMap(): polls GET /health/auth/locations every 60s
 *   Returns a locationId -> status map. No auth required.
 *
 * - useRevalidateToken(): mutation for POST /health/auth/refresh/:locationId
 *   Force-refreshes a specific location's token. No auth required.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'

const MIDDLEWARE_URL = import.meta.env.VITE_GHL_MIDDLEWARE_URL as string | undefined | null

/** How often to re-poll token health. Tokens change rarely — 60s is sufficient. */
const TOKEN_HEALTH_POLL_INTERVAL_MS = 60 * 1000

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type TokenStatus = 'healthy' | 'expiring_soon' | 'expired' | 'unknown'

export interface LocationTokenStatus {
  readonly status: 'healthy' | 'expiring_soon' | 'expired'
  readonly minutesUntilExpiry: number
  readonly expiresAt: string
}

export interface TokenHealthMap {
  readonly locations: Record<string, LocationTokenStatus>
}

export interface RevalidateResult {
  readonly success: boolean
  readonly locationId: string
  readonly status?: LocationTokenStatus['status']
  readonly minutesUntilExpiry?: number
  readonly expiresAt?: string
}

export class RevalidateError extends Error {
  readonly requiresReinstall: boolean

  constructor(message: string, requiresReinstall: boolean) {
    super(message)
    this.name = 'RevalidateError'
    this.requiresReinstall = requiresReinstall
  }
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

/**
 * Fetches per-location token health from the unauthenticated middleware endpoint.
 * Returns null if the middleware URL is not configured or the request fails.
 */
export function useTokenHealthMap(enabled = true) {
  return useQuery<TokenHealthMap | null>({
    queryKey: ['admin', 'ghl-token-health'],
    queryFn: async () => {
      if (!MIDDLEWARE_URL) return null

      const response = await fetch(`${MIDDLEWARE_URL}/health/auth/locations`, {
        method: 'GET',
        signal: AbortSignal.timeout(5000),
      })

      if (!response.ok) return null

      const data = await response.json() as { status: string } & TokenHealthMap
      return { locations: data.locations ?? {} }
    },
    refetchInterval: TOKEN_HEALTH_POLL_INTERVAL_MS,
    staleTime: 30 * 1000,
    enabled: enabled && !!MIDDLEWARE_URL,
    retry: false,
  })
}

/**
 * Mutation to force-refresh a specific location's GHL OAuth token.
 * Invalidates the token health cache on success so the badge updates immediately.
 */
export function useRevalidateToken() {
  const queryClient = useQueryClient()

  return useMutation<RevalidateResult, RevalidateError, string>({
    mutationFn: async (locationId: string) => {
      if (!MIDDLEWARE_URL) {
        throw new RevalidateError('Middleware URL not configured', false)
      }

      const response = await fetch(
        `${MIDDLEWARE_URL}/health/auth/refresh/${encodeURIComponent(locationId)}`,
        {
          method: 'POST',
          signal: AbortSignal.timeout(15_000), // refresh can take several seconds
        }
      )

      const body = await response.json() as {
        success: boolean
        error?: string
        requiresReinstall?: boolean
        locationId: string
        status?: LocationTokenStatus['status']
        minutesUntilExpiry?: number
        expiresAt?: string
      }

      if (!response.ok) {
        throw new RevalidateError(
          body.error ?? `Refresh failed (${response.status})`,
          body.requiresReinstall ?? false
        )
      }

      return body as RevalidateResult
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'ghl-token-health'] })
    },
  })
}
